/**
 * Safe Accessors Utility
 * 
 * These helpers prevent "Cannot read property 'X' of undefined" errors
 * by providing safe defaults for array/object operations.
 * 
 * Usage:
 *   import { safeArray, safeFilter, safeFind, safeMap } from '../utils/safeAccessors';
 *   
 *   // Instead of: clients.filter(c => c.active)
 *   // Use: safeFilter(clients, c => c.active)
 */

/**
 * Returns the array if it exists, otherwise returns empty array
 * @param {Array|null|undefined} arr - The array to check
 * @returns {Array} - The original array or empty array
 */
export const safeArray = (arr) => arr || [];

/**
 * Safely filter an array - returns empty array if input is null/undefined
 * @param {Array|null|undefined} arr - The array to filter
 * @param {Function} predicate - The filter function
 * @returns {Array} - Filtered array or empty array
 */
export const safeFilter = (arr, predicate) => (arr || []).filter(predicate);

/**
 * Safely find in an array - returns undefined if input is null/undefined
 * @param {Array|null|undefined} arr - The array to search
 * @param {Function} predicate - The find function
 * @returns {*} - Found item or undefined
 */
export const safeFind = (arr, predicate) => (arr || []).find(predicate);

/**
 * Safely map an array - returns empty array if input is null/undefined
 * @param {Array|null|undefined} arr - The array to map
 * @param {Function} mapper - The map function
 * @returns {Array} - Mapped array or empty array
 */
export const safeMap = (arr, mapper) => (arr || []).map(mapper);

/**
 * Safely get array length - returns 0 if input is null/undefined
 * @param {Array|null|undefined} arr - The array to check
 * @returns {number} - Array length or 0
 */
export const safeLength = (arr) => (arr || []).length;

/**
 * Safely check if array includes a value
 * @param {Array|null|undefined} arr - The array to check
 * @param {*} value - The value to find
 * @returns {boolean} - True if found, false otherwise
 */
export const safeIncludes = (arr, value) => (arr || []).includes(value);

/**
 * Safely check if array has any items matching predicate
 * @param {Array|null|undefined} arr - The array to check
 * @param {Function} predicate - The test function
 * @returns {boolean} - True if any match, false otherwise
 */
export const safeSome = (arr, predicate) => (arr || []).some(predicate);

/**
 * Safely check if all items in array match predicate
 * @param {Array|null|undefined} arr - The array to check
 * @param {Function} predicate - The test function
 * @returns {boolean} - True if all match (or array is empty), false otherwise
 */
export const safeEvery = (arr, predicate) => (arr || []).every(predicate);

/**
 * Safely access nested object property
 * @param {Object} obj - The object to access
 * @param {string} path - Dot-separated path (e.g., 'user.profile.name')
 * @param {*} defaultValue - Default value if path doesn't exist
 * @returns {*} - The value at path or defaultValue
 */
export const safeGet = (obj, path, defaultValue = undefined) => {
  if (!obj || !path) return defaultValue;
  
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result === null || result === undefined) {
      return defaultValue;
    }
    result = result[key];
  }
  
  return result === undefined ? defaultValue : result;
};

/**
 * Safely concatenate arrays - handles null/undefined inputs
 * @param {...Array} arrays - Arrays to concatenate
 * @returns {Array} - Concatenated array
 */
export const safeConcat = (...arrays) => {
  return arrays.reduce((acc, arr) => [...acc, ...(arr || [])], []);
};

// Default export for convenience
export default {
  safeArray,
  safeFilter,
  safeFind,
  safeMap,
  safeLength,
  safeIncludes,
  safeSome,
  safeEvery,
  safeGet,
  safeConcat
};
